// Função pura: decide quais avisos do Portal do Consumidor ainda estão pendentes
// para o consumidor logado, e em que ordem exibi-los. Não faz fetch nem depende do
// Supabase — quem chama (PortalConsumidorGate ou AvisosConsumidorView) já filtrou
// os dados pela query/RLS; aqui reaplicamos vigência e confirmação porque o painel
// de Config também passa avisos em qualquer status, sem o filtro de status='publicado'
// que a RLS aplica só para o consumidor.
//
// Não existe aqui resolução de "cadeia de versões": editar um aviso publicado nunca
// faz UPDATE no texto (ver views/Config/AvisosConsumidorView.tsx) — cria uma linha
// nova e arquiva a antiga. A antiga some do conjunto de avisos publicados assim que
// é arquivada, e a nova (id novo, sem confirmação do consumidor ainda) volta a
// aparecer como pendente naturalmente, sem lógica extra aqui.

export interface AvisoParaResolucao {
    id: string;
    bloqueante: boolean;
    data_inicio: string | null; // ISO
    data_fim: string | null; // ISO
}

export interface ConfirmacaoAviso {
    aviso_id: string;
    visualizado_em: string | null;
    confirmado_em: string | null;
}

export interface AvisoPendente<T extends AvisoParaResolucao> {
    aviso: T;
}

const dentroDaVigencia = (aviso: AvisoParaResolucao, agora: Date): boolean => {
    if (aviso.data_inicio && new Date(aviso.data_inicio) > agora) return false;
    if (aviso.data_fim && new Date(aviso.data_fim) < agora) return false;
    return true;
};

/**
 * Avisos que o consumidor ainda não confirmou, dentro da vigência, ordenados com
 * os bloqueantes primeiro (mais antigo por data_inicio primeiro), depois os
 * não-bloqueantes na mesma ordem. `agora` é parâmetro (não `new Date()` interno)
 * para o resultado ser determinístico em teste.
 */
export function resolverAvisosPendentes<T extends AvisoParaResolucao>(
    avisos: T[],
    confirmacoes: ConfirmacaoAviso[],
    agora: Date
): AvisoPendente<T>[] {
    const confirmadoPorAviso = new Set(
        confirmacoes.filter((c) => c.confirmado_em !== null).map((c) => c.aviso_id)
    );

    const pendentes = avisos.filter(
        (aviso) => dentroDaVigencia(aviso, agora) && !confirmadoPorAviso.has(aviso.id)
    );

    const porDataInicio = (a: T, b: T) => {
        const ta = a.data_inicio ? new Date(a.data_inicio).getTime() : -Infinity;
        const tb = b.data_inicio ? new Date(b.data_inicio).getTime() : -Infinity;
        return ta - tb;
    };

    const bloqueantes = pendentes.filter((a) => a.bloqueante).sort(porDataInicio);
    const naoBloqueantes = pendentes.filter((a) => !a.bloqueante).sort(porDataInicio);

    return [...bloqueantes, ...naoBloqueantes].map((aviso) => ({ aviso }));
}
